import { Button, Divider, Form, Input, message, Modal, Radio, Space } from "antd";
import React, { FC, useState } from "react";
import { renderErrorMessage } from "../../../../components/messages/errorMessage";
import DividerLine from "../../../../components/lineSplit";
import SavedMessage from "../../../../components/messages/savedMessage";
import LeasingStore from "../../../../contexts/leasingStore";
import _t from "../../../../lang/translate";
import { activateLeasingCase, expireLeasingCase } from "../../../../services/leasingService";
import LeasingAdminDealActiveInfo from "./leasingAdminDealActiveInfo";
import LeasingAdminDealExpireInfo from "./leasingAdminDealExpireInfo";
import { convertToApiFileList } from "../../../../utilities/convertTypes";
import { UploadFile } from "antd/lib/upload/interface";
import DocumentsUpload from "../../../../components/documentsUpload";
import { mediaApiEndpoint } from "../../../../services/mediaService";
import InputPrice from "../../../../components/form/inputPrice";
import Icon from "../../../../assets/icons/icon";
import PopoverList from "../../../../components/popups/popoverList";

type ActionType = "expired" | "activated";

type FormType = {
  note: string,
  registration: string,
  registrationHandler: string,
  numberPlateSource: string,
  expirationAction: string,
  files: UploadFile[] | null;
  vehicleReceipt: UploadFile[] | null;
  platesReceipt: UploadFile[] | null;
  cancellationReceipt: UploadFile[] | null;
  regulationReceipt: UploadFile[] | null;
  platesCode: string | null;
  platesPrice: string | null;
}

type Props = {
  type?: ActionType
}
const LeasingActivationActions: FC<Props> = ({ type }) => {
  const [loading, setLoading] = useState<boolean>(false);
  const { leasingCase, setLeasingCase, adminValues } = LeasingStore.useContainer();
  const [actionType, setActionType] = useState<ActionType | null>(null);
  const [form] = Form.useForm<FormType>();
  const { active, expired, readonly } = leasingCase.flags;
  const { motorAgency } = adminValues || {};
  const disabled = loading;

  const normFile = (e: any) => {
    if (Array.isArray(e)) {
      return e;
    }
    return e && e.fileList;
  };

  const handleAction = async (type: ActionType) => {
    const { id } = leasingCase;
    try {
      const values = form.getFieldsValue();
      const note = values.note
      setLoading(true);
      const { data } = type === "activated"
        ? await activateLeasingCase(id, {
          note,
          inquiries: [
            { question: "Registrering", answer: values.registration },
            { question: "Hvem foretager registrering?", answer: values.registrationHandler },
            { question: "Hvor afhentes der nummerplader?", answer: values.numberPlateSource },
          ],
          files: convertToApiFileList(values?.files || []),
          vehicleReceipt: values.vehicleReceipt ? convertToApiFileList(values.vehicleReceipt)[0] : null,
          platesReceipt: values.platesReceipt ? convertToApiFileList(values.platesReceipt)[0] : null,
          platesCode: values.platesCode,
          platesPrice: values.platesPrice ? parseInt(values.platesPrice) : null,
          platesInternalSource: values.numberPlateSource === "DMF",
        })
        : await expireLeasingCase(id, {
          note,
          inquiries: [{ question: 'Hvad sker der ved udløb?', answer: values.expirationAction }],
          files: convertToApiFileList(values?.files || []),
          cancellationReceipt: values.cancellationReceipt ? convertToApiFileList(values.cancellationReceipt)[0] : null,
          regulationReceipt: values.regulationReceipt ? convertToApiFileList(values.regulationReceipt)[0] : null,
        });
      setLeasingCase({ ...leasingCase, ...data });
      message.success(<SavedMessage />);
      setActionType(null);
    } catch (error) {
      renderErrorMessage(error)
    } finally {
      setLoading(false);
    }
  };

  let modalInfo = null;
  if (actionType === "activated") {
    modalInfo = <LeasingAdminDealActiveInfo />
  } else if (actionType === "expired") {
    modalInfo = <LeasingAdminDealExpireInfo />
  }

  return (
    <>
      <Space>
        {(type === "expired" || !type) && (
          <Button type="primary" disabled={disabled} onClick={() => setActionType("expired")} danger
            icon={<Icon name="hourglass-outline" />}
          >
            {expired ? _t("expire_leasing_deal_again") : _t("expire_leasing_deal")}
          </Button>
        )}
        {(type === "activated" || !type) && (
          <PopoverList list={[
            readonly ? _t("msg.case_is_readonly") : null,
          ].filter(Boolean) as string[]}>
            <Button type="primary" disabled={disabled || readonly} onClick={() => setActionType('activated')} color="green"
              icon={<Icon name="seal-check" />}
            >
              {active ? _t("activate", "again") : _t("activate", "deal")}
            </Button>
          </PopoverList>
        )}
      </Space>

      <Modal
        visible={!!actionType}
        confirmLoading={loading}
        onOk={() => actionType && handleAction(actionType)}
        okText={actionType === "activated" ? _t("activate", "deal") : _t("expire", "deal")}
        onCancel={() => setActionType(null)}
        cancelButtonProps={{ danger: true, type: "primary" }}
        title={_t('confirm_please')}
        width={800}
        className="modal-justify-between-footer"
      >
        {modalInfo}
        <Divider />
        <Form form={form}
          layout="horizontal"
          labelCol={{ xs: 24, lg: 10, xl: 8, className: "text-left label-h-auto", }}
          wrapperCol={{ xs: 24, lg: 14, xl: 16 }}
          initialValues={{
            registration: "Omregistrering",
            registrationHandler: "DMF",
            numberPlateSource: "DMF",
            expirationAction: "På lager",
            files: null,
            vehicleReceipt: null,
            platesReceipt: null,
            cancellationReceipt: null,
            regulationReceipt: null,
            platesCode: motorAgency?.registrationNumber,
            platesPrice: null,
            platesInternalSource: false
          }}
        >
          {actionType === "activated" ? (
            <>
              <Form.Item label="Registrering" rules={[{ required: true }]} name={["registration"]}>
                <Radio.Group
                  optionType="button"
                  buttonStyle={"solid"}
                  disabled={loading}
                >
                  <Radio.Button value={'Omregistrering'}>Omregistrering</Radio.Button>
                  <Radio.Button value={'Nye plader'}>Nye plader</Radio.Button>
                </Radio.Group>
              </Form.Item>

              <Form.Item label={"Hvem foretager registrering?"} rules={[{ required: true }]}
                name={["registrationHandler"]}>
                <Radio.Group
                  optionType="button"
                  buttonStyle={"solid"}
                  disabled={loading}
                >
                  <Radio.Button value={'DMF'}>DMF</Radio.Button>
                  <Radio.Button value={'Forhandler'}>Forhandler</Radio.Button>
                  <Radio.Button value={'Synshal'}>Synshal</Radio.Button>
                </Radio.Group>
              </Form.Item>
              <Form.Item label={"Hvor afhentes der nummerplader?"} rules={[{ required: true }]}
                name={["numberPlateSource"]}>
                <Radio.Group
                  optionType="button"
                  buttonStyle={"solid"}
                  disabled={loading}
                >
                  <Radio.Button value={'DMF'}>DMF</Radio.Button>
                  <Radio.Button value={'Forhandler'}>Forhandler</Radio.Button>
                  <Radio.Button value={'Synshal'}>Synshal</Radio.Button>
                </Radio.Group>
              </Form.Item>

              <DividerLine style={{ marginBlock: 16 }} />
              <Form.Item name="vehicleReceipt" label={_t("receipt", "create_leasing_agreement", ' - ')}
                valuePropName="fileList" getValueFromEvent={normFile}>
                <DocumentsUpload
                  multiple={false}
                  canLockFiles={false}
                  isModal={true}
                  action={mediaApiEndpoint}
                  canDelete={true}
                />
              </Form.Item>
              <Form.Item name="platesReceipt" label={_t("receipt", "register_vehicle", ' - ')}
                valuePropName="fileList" getValueFromEvent={normFile}>
                <DocumentsUpload
                  multiple={false}
                  canLockFiles={false}
                  isModal={true}
                  action={mediaApiEndpoint}
                  canDelete={true}
                />
              </Form.Item>
              <Form.Item name="platesCode" label={_t("registration_number")}>
                <Input />
              </Form.Item>
              <Form.Item name="platesPrice" label={_t("registeration_cost_nr_plates")}>
                <InputPrice decimalSize={0} suffix={"kr"} maxLength={10} />
              </Form.Item>
            </>
          ) : (
            <>
              <Form.Item label="Hvad sker der ved udløb?" rules={[{ required: true }]} name={["expirationAction"]}>
                <Radio.Group
                  optionType="button"
                  buttonStyle={"solid"}
                  disabled={loading}
                >
                  <Radio.Button value={'På lager'}>På lager</Radio.Button>
                  <Radio.Button value={'Solgt'}>Solgt</Radio.Button>
                  <Radio.Button value={'Debitorskifte'}>Debitorskifte</Radio.Button>
                  <Radio.Button value={'Ny aftale'}>Ny aftale</Radio.Button>
                </Radio.Group>
              </Form.Item>
              <DividerLine style={{ marginBlock: 16 }} />
              <Form.Item name="cancellationReceipt" label={_t("receipt", "cancel_leasing_agreement", ' - ')}
                valuePropName="fileList" getValueFromEvent={normFile}>
                <DocumentsUpload
                  canLockFiles={false}
                  isModal={true}
                  action={mediaApiEndpoint}
                  canDelete={true}
                />
              </Form.Item>
              <Form.Item name="regulationReceipt" label={_t("receipt", "regulate_leasing_fee", ' - ')}
                valuePropName="fileList" getValueFromEvent={normFile}>
                <DocumentsUpload
                  canLockFiles={false}
                  isModal={true}
                  action={mediaApiEndpoint}
                  canDelete={true}
                />
              </Form.Item>
            </>
          )}
          <DividerLine style={{ marginBlock: 16 }} />
          <Form.Item label={_t("eventually", "note_to_bookkeeping")} name="note">
            <Input.TextArea />
          </Form.Item>
          <Form.Item name="files" label={_t("further", "documents")} valuePropName="fileList"
            getValueFromEvent={normFile}>
            <DocumentsUpload
              canLockFiles={false}
              isModal={true}
              action={mediaApiEndpoint}
              canDelete={true}
              multiple={true}
            />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
};

export default LeasingActivationActions;
